import { APP_NAME } from '../global/config';

const LEGACY_KEY_PREFIX = 'PMFormsSession';
const DEFAULT_KEY_PREFIX = APP_NAME;

type Nullable<T> = T | null;

interface LocalStorageItemOptions {
  keyPrefix?: string;
}

type SetOptions = object;

export class LocalStorageItem<TValue> {
  private key: string;
  private keyPrefix: string;
  private cachedValue: Nullable<TValue> | undefined;
  private subscribers: Set<() => void>;

  constructor(key: string, options?: LocalStorageItemOptions) {
    this.key = key;
    this.keyPrefix = options?.keyPrefix || DEFAULT_KEY_PREFIX;
    this.subscribers = new Set();
  }

  get = (): Nullable<TValue> => {
    if (this.cachedValue !== undefined) return this.cachedValue;

    let jsonValue = localStorage.getItem(`${this.keyPrefix}.${this.key}`);

    // TODO - this block is for backwards compatibility. We should be able to remove this in a future release
    //        also if we decide to deploy Portal v2 at a different URL, then we won't need to do this anyways
    if (!jsonValue && this.keyPrefix !== LEGACY_KEY_PREFIX) {
      jsonValue = localStorage.getItem(`${LEGACY_KEY_PREFIX}.${this.key}`);
      // if we did fallback to legacy and got a value, let's remove the legacy key/value and update the new key/value
      if (jsonValue) {
        localStorage.setItem(`${this.keyPrefix}.${this.key}`, jsonValue);
        localStorage.removeItem(`${LEGACY_KEY_PREFIX}.${this.key}`);
      }
    }

    const parsedValue = jsonValue && JSON.parse(jsonValue);
    this.cachedValue = parsedValue;
    return parsedValue;
  };

  set = (value: Nullable<TValue>, _options?: SetOptions): void => {
    this.cachedValue = value || null;
    localStorage.setItem(`${this.keyPrefix}.${this.key}`, JSON.stringify(this.cachedValue));

    this.subscribers.forEach((callback) => callback());
  };

  subscribe = (callback: () => void) => {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  };
}
